import { SupportService } from "../../../services/support.service.js";

export const SupportMessengerModal = async () => {
    // 1. Create Modal Container
    const overlay = document.createElement('div');
    overlay.className = 'fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 opacity-0 transition-opacity duration-300';

    // 2. Modal Content (Messenger Window)
    const modal = document.createElement('div');
    modal.className = 'w-full h-[80vh] sm:max-w-md sm:h-[600px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden transform translate-y-full sm:translate-y-10 transition-transform duration-300';

    // Header
    const header = document.createElement('div');
    header.className = 'bg-indigo-600 p-4 text-white flex items-center justify-between shadow-md z-10';
    header.innerHTML = `
        <div class="flex items-center gap-2">
            <span class="material-symbols-outlined">support_agent</span>
            <h2 class="font-bold text-lg">${window.locale.t('support.title')}</h2>
        </div>
        <button id="close-support-btn" class="p-1 hover:bg-white/20 rounded-full transition-colors">
            <span class="material-symbols-outlined">close</span>
        </button>
    `;

    // Chat Body (Message List)
    const body = document.createElement('div');
    body.className = 'flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4 scroll-smooth';
    body.id = 'support-chat-body';

    // Footer (Input)
    const footer = document.createElement('div');
    footer.className = 'p-3 bg-white border-t border-gray-200 flex gap-2 items-end';
    footer.innerHTML = `
        <textarea id="support-input" rows="1" class="flex-1 resize-none bg-gray-100 border-0 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 text-sm max-h-32" placeholder="${window.locale.t('support.input_placeholder')}"></textarea>
        <button id="support-send-btn" class="p-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm mb-0.5">
            <span class="material-symbols-outlined text-[20px] block">send</span>
        </button>
    `;

    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // --- Logic ---
    let unsubscribe = null;
    const currentUser = window.user; // Assumption: Validated before opening

    // Close Handler
    const close = () => {
        overlay.classList.remove('opacity-100');
        modal.classList.remove('translate-y-0');
        setTimeout(() => {
            if (unsubscribe) unsubscribe();
            overlay.remove();
        }, 300);
    };

    header.querySelector('#close-support-btn').onclick = close;
    overlay.onclick = (e) => { if (e.target === overlay) close(); };

    // Render Message Helper
    const renderMessage = (ticket) => {
        // 1. User Message (Right)
        const userMsgHtml = `
            <div class="flex justify-end mb-1">
                <div class="max-w-[80%] bg-indigo-600 text-white px-4 py-2.5 rounded-2xl rounded-tr-sm shadow-sm text-sm break-words whitespace-pre-wrap">${ticket.content}</div>
            </div>
            <div class="flex justify-end mb-4">
                 <span class="text-[10px] text-gray-400">
                    ${ticket.createdAt ? ticket.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                    ${ticket.repliedAt ? '' : (window.locale.t('support.status_received') || " • 접수됨")}
                 </span>
            </div>
        `;

        // 2. Admin Reply (Left) - If exists
        let adminMsgHtml = '';

        // [Auto-Reply Logic]
        // If there is an actual reply -> Show it.
        // If NO reply yet -> Show the Auto-Reply "Receipt Confirmed" message.
        // This ensures it persists even after real-time updates overwrite the DOM.
        const replyContent = ticket.replyContent || (window.locale.t('support.auto_reply') || "의견이 접수되었습니다. 문의 내용은 순차적으로 답변드립니다. 그 동안 썰화월드를 여행해주세요.");
        const replyTime = ticket.replyAt ? ticket.replyAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

        // Visual distinction: Actual reply vs Auto-Reply?
        // User requested "Auto Reply" so it should look like a reply. 
        // Maybe slightly different color? No, standard is fine.

        adminMsgHtml = `
            <div class="flex justify-start mb-1">
                 <div class="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2 flex-shrink-0">
                    <span class="material-symbols-outlined text-indigo-600 text-sm">support_agent</span>
                 </div>
                <div class="max-w-[75%] bg-white border border-gray-200 text-gray-800 px-4 py-2.5 rounded-2xl rounded-tl-sm shadow-sm text-sm break-words whitespace-pre-wrap">${replyContent}</div>
            </div>
             <div class="flex justify-start ml-10 mb-4">
                 <span class="text-[10px] text-gray-400">
                    ${replyTime}
                 </span>
            </div>
        `;

        const wrapper = document.createElement('div');
        wrapper.innerHTML = userMsgHtml + adminMsgHtml;
        return wrapper;
    };

    // Load & Subscribe Logic
    if (currentUser) {
        // Initial Loading State
        body.innerHTML = '<div class="flex justify-center py-10"><span class="animate-spin material-symbols-outlined text-gray-400">progress_activity</span></div>';

        unsubscribe = SupportService.subscribeMyTickets(currentUser.uid, (tickets) => {
            body.innerHTML = '';

            if (tickets.length === 0) {
                body.innerHTML = `
                    <div class="h-full flex flex-col items-center justify-center text-gray-400 opacity-70">
                        <span class="material-symbols-outlined text-4xl mb-2">forum</span>
                        <p class="text-xs">${window.locale.t('support.empty_state_title')}<br>${window.locale.t('support.empty_state_desc')}</p>
                    </div>
                `;
            } else {
                tickets.forEach(ticket => body.appendChild(renderMessage(ticket)));
                // Scroll to bottom
                requestAnimationFrame(() => {
                    body.scrollTop = body.scrollHeight;
                });
            }
        });
    } else {
        window.Modal.toast(window.locale.t('auth.login_required'));
        close();
        return;
    }

    // Send Logic
    const input = footer.querySelector('#support-input');
    const sendBtn = footer.querySelector('#support-send-btn');

    const handleSend = async () => {
        const text = input.value.trim();
        if (!text) return;

        sendBtn.disabled = true;
        try {
            await SupportService.createTicket(currentUser.uid, text);
            input.value = '';
            input.style.height = 'auto'; // Reset height

            // Note: Auto-Reply is now handled by renderMessage based on state.
            // When the Firestore listener updates with the new ticket, it will automatically render the reply.

            // Scroll to bottom
            requestAnimationFrame(() => {
                body.scrollTop = body.scrollHeight;
            });
        } catch (e) {
            console.error(e);
            window.Modal.toast(window.locale.t('support.send_fail'));
        } finally {
            sendBtn.disabled = false;
            // Focus back? Mobile keyboard handling is tricky, maybe strictly keep focus?
            input.focus();
        }
    };

    sendBtn.onclick = handleSend;

    // Auto Resize Textarea
    input.addEventListener('input', function () {
        this.style.height = 'auto';
        this.style.height = (this.scrollHeight) + 'px';
    });

    // Reveal Animation
    requestAnimationFrame(() => {
        overlay.classList.add('opacity-100');
        modal.classList.add('translate-y-0');
        setTimeout(() => input.focus(), 350);
    });
};
